// ============================================================
// src/hooks/useTwilioDevice.ts
// ============================================================
 
import { useState, useEffect, useCallback, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { useAuth } from '@/contexts/AuthContext';
import { callsApi } from '@/services/api/calls.api';
import { toast } from 'sonner';
 
export type SoftphoneStatus = 'offline' | 'initializing' | 'ready' | 'busy' | 'error';
export type CallDirection = 'outbound' | 'inbound';
 
export interface ActiveCall {
  call: Call;
  direction: CallDirection;
  to: string;
  from: string;
  status: string;
  startedAt: Date;
  isMuted: boolean;
}
 
interface UseTwilioDeviceOptions {
  autoRegister?: boolean;
}
 
export function useTwilioDevice(options: UseTwilioDeviceOptions = {}) {
  const { autoRegister = false } = options;
  const { user } = useAuth();
  const deviceRef = useRef<any>(null);
  const [deviceStatus, setDeviceStatus] = useState<SoftphoneStatus>('offline');
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
 
  // ── Fetch token via Cloud Run getvoicetoken ──────────────────
 
  const fetchToken = useCallback(async (): Promise<string | null> => {
    if (!user?.email) return null;
    try {
      const { token } = await callsApi.getToken();
      return token || null;
    } catch (err) {
      console.error('Failed to fetch Twilio token:', err);
      return null;
    }
  }, [user?.email]);
 
  // ── Timer ────────────────────────────────────────────────────
 
  const startTimer = useCallback(() => {
    setCallDuration(0);
    timerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
  }, []);
 
  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);
 
  // ── Initialiser le device Twilio ─────────────────────────────
 
  const initializeDevice = useCallback(async () => {
    if (deviceRef.current) deviceRef.current.destroy();
    setDeviceStatus('initializing');
 
    const token = await fetchToken();
    if (!token) {
      setDeviceStatus('error');
      toast.error('Impossible d\'initialiser le téléphone — token manquant');
      return false;
    }
 
    try {
      const device = new Device(token, { edge: 'ashburn' });
 
      device.on('registered', () => { setDeviceStatus('ready'); toast.success('Téléphone connecté'); });
      device.on('unregistered', () => setDeviceStatus('offline'));
      device.on('error', (err: any) => { setDeviceStatus('error'); toast.error(`Erreur téléphone: ${err.message}`); });
      device.on('tokenWillExpire', async () => {
        const newToken = await fetchToken();
        if (newToken) device.updateToken(newToken);
      });
 
      device.on('incoming', (call: Call) => {
        setIncomingCall(call);
        toast.info(`📞 Appel entrant: ${call.parameters.From}`, { duration: 15000 });
        call.on('cancel', () => { setIncomingCall(null); toast.info('Appel manqué'); });
        call.on('disconnect', () => { setIncomingCall(null); setActiveCall(null); setDeviceStatus('ready'); stopTimer(); });
      });
 
      await device.register();
      deviceRef.current = device;
      return true;
    } catch (err) {
      console.error('Failed to initialize Twilio Device:', err);
      setDeviceStatus('error');
      toast.error('Échec de l\'initialisation du téléphone');
      return false;
    }
  }, [fetchToken, stopTimer]);
 
  // ── Appel sortant ────────────────────────────────────────────
 
  const makeCall = useCallback(async (to: string): Promise<boolean> => {
    let device = deviceRef.current;
    if (!device || deviceStatus !== 'ready') {
      const initialized = await initializeDevice();
      if (!initialized) return false;
      device = deviceRef.current;
      if (!device) return false;
    }
 
    try {
      setDeviceStatus('busy');
      let formattedTo = to.replace(/[^\d+]/g, '');
      if (!formattedTo.startsWith('+'))
        formattedTo = formattedTo.startsWith('1') ? `+${formattedTo}` : `+1${formattedTo}`;
 
      const call = await device.connect({ params: { To: formattedTo } });
 
      setActiveCall({ call, direction: 'outbound', to: formattedTo, from: 'agent', status: 'connecting', startedAt: new Date(), isMuted: false });
 
      call.on('accept', () => { setActiveCall(prev => prev ? { ...prev, status: 'in-progress' } : null); startTimer(); toast.success('Appel connecté'); });
      call.on('ringing', () => setActiveCall(prev => prev ? { ...prev, status: 'ringing' } : null));
      call.on('disconnect', () => { setActiveCall(null); setDeviceStatus('ready'); stopTimer(); toast.info('Appel terminé'); });
      call.on('cancel', () => { setActiveCall(null); setDeviceStatus('ready'); stopTimer(); });
      call.on('error', (err: any) => { setActiveCall(null); setDeviceStatus('ready'); stopTimer(); toast.error(`Erreur d'appel: ${err.message}`); });
 
      return true;
    } catch (err) {
      console.error('Failed to make call:', err);
      setDeviceStatus('ready');
      toast.error('Échec de la connexion');
      return false;
    }
  }, [deviceStatus, initializeDevice, startTimer, stopTimer]);
 
  const acceptCall = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.accept();
    setActiveCall({ call: incomingCall, direction: 'inbound', to: 'agent', from: incomingCall.parameters.From || 'Inconnu', status: 'in-progress', startedAt: new Date(), isMuted: false });
    setIncomingCall(null); setDeviceStatus('busy'); startTimer();
  }, [incomingCall, startTimer]);
 
  const rejectCall = useCallback(() => { if (incomingCall) { incomingCall.reject(); setIncomingCall(null); } }, [incomingCall]);
  const hangUp = useCallback(() => { if (activeCall?.call) activeCall.call.disconnect(); setActiveCall(null); setDeviceStatus('ready'); stopTimer(); }, [activeCall, stopTimer]);
  const toggleMute = useCallback(() => {
    if (!activeCall?.call) return;
    const newMuted = !activeCall.isMuted;
    activeCall.call.mute(newMuted);
    setActiveCall(prev => prev ? { ...prev, isMuted: newMuted } : null);
  }, [activeCall]);
  const sendDigit = useCallback((digit: string) => { if (activeCall?.call) activeCall.call.sendDigits(digit); }, [activeCall]);
 
  useEffect(() => () => { if (deviceRef.current) { deviceRef.current.destroy(); deviceRef.current = null; } stopTimer(); }, [stopTimer]);
  useEffect(() => { if (autoRegister && user?.email && deviceStatus === 'offline') initializeDevice(); }, [autoRegister, user?.email, deviceStatus, initializeDevice]);
 
  const formattedDuration = `${Math.floor(callDuration / 60).toString().padStart(2, '0')}:${(callDuration % 60).toString().padStart(2, '0')}`;
 
  return { deviceStatus, activeCall, incomingCall, callDuration, formattedDuration, initializeDevice, makeCall, acceptCall, rejectCall, hangUp, toggleMute, sendDigit };
}
 